// Job list PDF export.
//
// Renders the filtered Job Inbox list as a downloadable PDF so owners
// can print/share from mobile — window.print() doesn't work reliably
// on iOS Safari or Chrome PWAs. The layout mirrors the on-screen
// "Print list" table: Date · Time · Broker/Rep · Route, one row per
// (parent + children) job, respecting the current sort order.

import jsPDF from "jspdf";

function formatScheduledShort(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
}

/**
 * Wrap a string into lines that fit `maxWidth` at the doc's current font.
 * Uses jsPDF's splitTextToSize which handles this correctly.
 */
function wrap(doc, text, maxWidth) {
  if (!text) return [];
  return doc.splitTextToSize(String(text), maxWidth);
}

/**
 * Build the printable rows from parentJobs + childMap in the SAME order
 * the on-screen table renders them (parent, then children sorted by
 * scheduledDate). One entry per job; children get an explicit inline
 * flag so the PDF matches the screen ordering.
 */
function buildRows(parentJobs, childMap) {
  const rows = [];
  for (const parent of parentJobs) {
    rows.push(parent);
    const children = (childMap?.[parent.id] || []).slice().sort((a, b) =>
      String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || ""))
    );
    for (const c of children) rows.push(c);
  }
  return rows;
}

/**
 * Draw a single job row. Returns the y-position AFTER the row so the
 * caller can walk down the page. Handles page breaks internally.
 */
function drawRow(doc, j, cols, y, pageBottom) {
  const dateStr = formatScheduledShort(j.scheduledDate) || "—";
  const timeStr = j.scheduledTime || "";
  const broker  = j.brokerName || "—";
  const rep     = j.salesRepName || "";
  const repPhone = j.salesRepPhone || "";

  const realStops = (j.stops || []).filter((s) => s.type !== "airport");
  // Wrap each cell to its column width so long route lists don't spill.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const brokerLines = wrap(doc, broker, cols.brokerRep.w - 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  // Plain text only — jsPDF's built-in fonts encode CP1252, so emoji
  // (👤/☎/↓ from the on-screen table) render as mojibake AND force the
  // whole line into 16-bit encoding, which letter-spaces the real text.
  const repLines = [];
  if (rep) repLines.push(...wrap(doc, `Rep: ${rep}`, cols.brokerRep.w - 4));
  if (repPhone) repLines.push(...wrap(doc, repPhone, cols.brokerRep.w - 4));

  const routeBlocks = [];
  if (realStops.length === 0) {
    routeBlocks.push({ nameLines: ["—"], addressLines: [] });
  } else {
    realStops.forEach((s, i) => {
      const prefix = i > 0 ? "> " : ""; // "↓" is outside CP1252 — see repLines note
      const name = s.outOfTown ? "OUT OF TOWN" : (s.locationName || "—");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      const nameLines = wrap(doc, `${prefix}${name}`, cols.route.w - 4);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      const addressLines = (!s.outOfTown && s.address)
        ? wrap(doc, s.address, cols.route.w - 4 - (i > 0 ? 6 : 0))
        : [];
      routeBlocks.push({ nameLines, addressLines });
    });
  }

  // Row height = max of column heights. All cells use ~10pt line height
  // regardless of the font size that generated them, so the alignment
  // stays consistent across columns.
  const lh = 10;
  const brokerRepLines = brokerLines.length + repLines.length;
  const routeLineCount = routeBlocks.reduce(
    (n, b) => n + b.nameLines.length + b.addressLines.length,
    0
  ) + Math.max(0, routeBlocks.length - 1); // gap between blocks
  const rowLines = Math.max(1, brokerRepLines, routeLineCount);
  const rowH = rowLines * lh + 6;

  // Page break?
  if (y + rowH > pageBottom) {
    doc.addPage();
    y = 40; // top margin on continuation pages
    // Reprint header row on continuation pages.
    y = drawHeader(doc, cols, y);
  }

  // Draw cells top-aligned.
  const topPad = 3;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(dateStr, cols.date.x + 2, y + topPad + 7);
  doc.text(timeStr, cols.time.x + 2, y + topPad + 7);

  // Broker + rep block
  let by = y + topPad + 7;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  brokerLines.forEach((line) => { doc.text(line, cols.brokerRep.x + 2, by); by += lh; });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  repLines.forEach((line) => { doc.text(line, cols.brokerRep.x + 2, by); by += lh; });

  // Route stops block
  let ry = y + topPad + 7;
  routeBlocks.forEach((block, i) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    block.nameLines.forEach((line) => { doc.text(line, cols.route.x + 2, ry); ry += lh; });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const addrIndent = i > 0 ? 6 : 0;
    block.addressLines.forEach((line) => { doc.text(line, cols.route.x + 2 + addrIndent, ry); ry += lh; });
    if (i < routeBlocks.length - 1) ry += 2; // small gap between stops
  });

  // Bottom border
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(cols.date.x, y + rowH, cols.route.x + cols.route.w, y + rowH);

  return y + rowH;
}

function drawHeader(doc, cols, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.6);
  doc.line(cols.date.x, y - 2, cols.route.x + cols.route.w, y - 2);
  const th = y + 9;
  doc.text("Date",         cols.date.x + 2,      th);
  doc.text("Time",         cols.time.x + 2,      th);
  doc.text("Broker · Rep", cols.brokerRep.x + 2, th);
  doc.text("Route",        cols.route.x + 2,     th);
  doc.line(cols.date.x, y + 14, cols.route.x + cols.route.w, y + 14);
  return y + 20;
}

/**
 * Generate the PDF blob for a set of jobs.
 *
 * @param {object[]} parentJobs — filtered/sorted parent jobs (already applied
 *   by the caller). Same value the on-screen table renders.
 * @param {object}   childMap — { parentJobId: [child jobs] }, used to expand
 *   DAI-style multi-detail bundles inline.
 * @param {object}   opts
 *   opts.title       — big header (e.g. "Direct Job Schedule").
 *   opts.filterLine  — small subheader summarizing applied filters.
 *   opts.filename    — download filename (defaults to job-list.pdf).
 */
export async function generateJobListPdf(parentJobs, childMap, opts = {}) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;
  const contentW = pageW - margin * 2;

  // Column layout — same 4 columns as the on-screen print table.
  const cols = {
    date:      { x: margin,                w: 70 },
    time:      { x: margin + 70,           w: 48 },
    brokerRep: { x: margin + 70 + 48,      w: Math.round(contentW * 0.30) },
    route:     { x: 0, w: 0 }, // filled below
  };
  cols.route.x = cols.brokerRep.x + cols.brokerRep.w;
  cols.route.w = pageW - margin - cols.route.x;

  // Title + filter line
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(opts.title || "Job Schedule", margin, margin + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  const gen = new Date().toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
  const filterLine = (opts.filterLine ? opts.filterLine + " · " : "") +
    `${parentJobs.length} job${parentJobs.length !== 1 ? "s" : ""} · Generated ${gen}`;
  doc.text(filterLine, margin, margin + 22);
  doc.setTextColor(0, 0, 0);

  let y = drawHeader(doc, cols, margin + 40);

  const pageBottom = pageH - margin;
  const rows = buildRows(parentJobs, childMap);
  if (rows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(11);
    doc.setTextColor(150, 150, 150);
    doc.text("No jobs match the current filters.", margin, y + 20);
    doc.setTextColor(0, 0, 0);
  } else {
    for (const j of rows) {
      y = drawRow(doc, j, cols, y, pageBottom);
    }
  }

  return doc.output("blob");
}

/** Trigger a download of the PDF. Filename defaults to job-list.pdf. */
export async function downloadJobListPdf(parentJobs, childMap, opts = {}) {
  const blob = await generateJobListPdf(parentJobs, childMap, opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = opts.filename || "job-list.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
